import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { useAuthStore, type UserRole } from '@/stores/auth';
import { Spinner } from '@/components/ui/spinner';
import { authT } from '@/i18n/messages/auth';
import type { ReactNode } from 'react';

interface AuthGuardProps {
  children: ReactNode;
  /// Role obrigatório. Se omitido, basta estar autenticado.
  requireRole?: UserRole;
}

export function AuthGuard({ children, requireRole }: AuthGuardProps) {
  const { user, loading } = useAuth();
  const hasRole = useAuthStore((s) => s.hasRole);
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner className="h-6 w-6" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  if (requireRole && !hasRole(requireRole)) {
    const t = authT().guard;
    return (
      <div className="min-h-screen flex items-center justify-center px-4 text-center">
        <div className="max-w-md space-y-3">
          <h2 className="text-2xl font-semibold">{t.restrictedTitle}</h2>
          <p className="text-sm text-muted-foreground">
            {t.restrictedBody(<code className="rounded bg-muted px-1">{requireRole}</code>)}
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
